import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { EntityKind } from '../routes'
import RecentService, { RecentEntry } from '../service/RecentService'
import WallexService from '../service/WallexService'

interface UseEntityOptions<T> {
    kind: EntityKind
    // Id from the URL; the URL is the source of truth for which entity is shown
    id?: string
    retrieve: (id: string) => Promise<T>
    pathFor: (id?: string) => string
    notFound: string
    // Extra details stored with the recent entry (label, related ids)
    remember?: (entity: T) => Omit<Partial<RecentEntry>, 'kind' | 'id' | 'region' | 'at'>
}

export interface EntityState<T> {
    entity: T | null
    setEntity: React.Dispatch<React.SetStateAction<T | null>>
    loading: boolean
    error: string | null
    // Opens the given id (or clears the page for an empty value)
    find: (value: string) => void
    refresh: () => void
}

export default function useEntity<T>({ kind, id, retrieve, pathFor, notFound, remember }: UseEntityOptions<T>): EntityState<T> {
    const navigate = useNavigate()
    const [entity, setEntity] = useState<T | null>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)
    // Ignores responses that arrive after a newer request was made
    const latestRequest = useRef(0)

    const load = (entityId: string) => {
        const request = ++latestRequest.current
        setLoading(true)
        setError(null)
        retrieve(entityId)
            .then((result) => {
                if (request !== latestRequest.current) return
                setEntity(result)
                RecentService.record({ kind, id: entityId, ...remember?.(result) })
            })
            .catch((e) => {
                if (request !== latestRequest.current) return
                setEntity(null)
                setError(WallexService.errorDescription(e, notFound))
            })
            .finally(() => {
                if (request === latestRequest.current) setLoading(false)
            })
    }

    useEffect(() => {
        setEntity(null)
        if (id) {
            load(id)
        } else {
            latestRequest.current++
            setError(null)
            setLoading(false)
        }
    }, [id])

    const find = (value: string) => {
        const entityId = value.trim()
        if (!entityId) navigate(pathFor())
        // Searching the current id again (e.g. after a failed fetch) won't change the URL, so load directly
        else if (entityId === id) load(entityId)
        else navigate(pathFor(entityId))
    }

    const refresh = () => {
        if (id) load(id)
    }

    return { entity, setEntity, loading, error, find, refresh }
}
